import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CONSENT_CHANGED, CONSENT_KEY } from '../analytics/ga4';

const BTN = 'inline-flex items-center justify-center border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors';

// First-visit cookie notice. Stays visible until the visitor accepts or declines;
// the choice is saved to localStorage and gates Google Analytics (see analytics/ga4.js).
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show the banner even when storage is blocked (strict privacy modes) —
    // a visitor who cannot store a choice still needs to see the notice.
    let hasChoice = false;
    try {
      hasChoice = Boolean(window.localStorage.getItem(CONSENT_KEY));
    } catch {
      hasChoice = false;
    }
    // Global Privacy Control: honor the browser's opt-out signal automatically —
    // record the decline and don't show the banner at all.
    if (navigator.globalPrivacyControl === true) {
      if (!hasChoice) {
        try {
          window.localStorage.setItem(
            CONSENT_KEY,
            JSON.stringify({ essential: true, analytics: false, marketing: false, gpc: true, updatedAt: new Date().toISOString() })
          );
          window.dispatchEvent(new Event(CONSENT_CHANGED));
        } catch {
          /* storage unavailable — ga4.js still blocks analytics on GPC */
        }
      }
      return undefined;
    }
    if (!hasChoice) {
      const t = window.setTimeout(() => setVisible(true), 1400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);

  const choose = (analytics, marketing) => {
    try {
      window.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ essential: true, analytics, marketing, updatedAt: new Date().toISOString() })
      );
      window.dispatchEvent(new Event(CONSENT_CHANGED));
    } catch {
      /* storage unavailable */
    }
    setVisible(false);
  };

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 border-t border-line bg-panel/95 backdrop-blur-md transition-all duration-700 ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'
      }`}
      role="dialog"
      aria-label="Cookie notice"
      aria-hidden={!visible}
      data-testid="cookie-banner"
    >
      <div className="mx-auto flex max-w-shell flex-col gap-4 px-5 py-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
        <p className="max-w-2xl text-xs leading-relaxed text-dim sm:text-sm">
          We use cookies and similar technologies to enhance your experience, analyze site traffic
          and deliver personalized content. Read our{' '}
          <Link to="/cookies" className="text-white underline decoration-line underline-offset-4 transition-colors hover:decoration-accent" data-testid="cookie-banner-policy-link">
            Cookie Policy
          </Link>{' '}
          for details.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={`${BTN} border-accent bg-accent text-white hover:brightness-110`} onClick={() => choose(true, true)} data-testid="cookie-banner-accept">
            Accept all
          </button>
          <button type="button" className={`${BTN} border-line text-white hover:border-accent`} onClick={() => choose(false, false)} data-testid="cookie-banner-decline">
            Decline non-essential
          </button>
          <Link to="/cookie-preferences" className={`${BTN} border-transparent text-mute hover:text-white`} onClick={() => setVisible(false)} data-testid="cookie-banner-manage">
            Manage choices
          </Link>
        </div>
      </div>
    </div>
  );
}
